import { FONT_FAMILIES } from '../types';
import type { ComponentSchema, KnobDef, KnobDefault } from './components';

/**
 * The desktop Custom Pack Lab. Its knob names and designer defaults mirror
 * `apps/iframe/src/modules/customPack/desktop/labTheme.ts`, which the iframe reads as
 * `var(--co-<name>-<knob>, <default>)`; a registry test fails when the two drift.
 *
 * Every default is a literal or another lab knob, never a palette role: an operator's palette
 * does not tint the lab until the operator sets a knob here. A knob that inherits another one
 * (`inheritKnob`) follows it until it is set, as `var(--co-New-k, var(--co-Old-k, …))` does in
 * the iframe.
 */

/** The sub-headings of the lab's group in the sidebar, in the order they are listed. */
export const LAB_SECTIONS = [
  'Shared',
  'Header',
  'Catalog',
  'Toolbar & filters',
  'Rail',
  'Odds',
  'Reveal',
  'Phone bar & sheet',
] as const;

export type LabSection = (typeof LAB_SECTIONS)[number];

const lit = (v: string | number): KnobDefault => ({ kind: 'literal', value: v });

const colorKnob = (label: string, value: string, hint?: string): KnobDef => ({
  kind: 'color',
  label,
  default: lit(value),
  ...(hint ? { hint } : {}),
});

const radiusKnob = (label: string, value: number, hint?: string): KnobDef => ({
  kind: 'radius',
  label,
  default: lit(value),
  ...(hint ? { hint } : {}),
});

/** A colour that follows another lab knob until the operator sets it. */
const inheritKnob = (label: string, component: string, knob: string, hint?: string): KnobDef => ({
  kind: 'color',
  label,
  default: { kind: 'componentKnob', component, knob },
  ...(hint ? { hint } : {}),
});

const labComponent = (
  name: string,
  part: string,
  group: ComponentSchema['group'],
  section: LabSection,
  description: string,
  knobs: Record<string, KnobDef>,
): ComponentSchema => ({
  name,
  displayName: `Custom Pack Lab — ${part}`,
  description,
  group,
  scenes: ['custom-pack'],
  section,
  knobs,
});

export const LAB_COMPONENTS: readonly ComponentSchema[] = [
  // ── Shared ──────────────────────────────────────────────────────────
  labComponent(
    'LabSurface',
    'Surface and shared',
    'Layout',
    'Shared',
    'The surface of the whole lab, its text colours, radii and font. Most other parts follow these until they are set.',
    {
      fill: colorKnob('Surface fill', '#050A00', 'The lab, edge to edge, and whatever floats over it: the filters popover and the modals.'),
      border: colorKnob('Surface border', 'rgba(245,245,245,0.09)'),
      text: colorKnob('Text', '#FFFFFF'),
      subtext: colorKnob('Subtext', 'rgba(255,255,255,0.5)', 'Subtitle, grade and placeholder text.'),
      danger: colorKnob('Error', '#FF6369', 'A failure the player can act on: a rip that failed, a card that is gone.'),
      shadow: colorKnob('Shadow colour', 'rgba(0,0,0,0.35)', 'Drop shadow of rows and tiles.'),
      radius: radiusKnob('Panel radius (px)', 18, 'The filters drawer, the sheets and the modals.'),
      cardRadius: radiusKnob('Card radius (px)', 12, 'Rail, rows, tiles and odds rows.'),
      pillRadius: radiusKnob('Pill radius (px)', 60, 'Pills, buttons, the search field and the call to action.'),
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
        hint: 'Every text of the lab.',
      },
      innerGlow: colorKnob(
        'Inner glow',
        'rgba(255,255,255,0.09)',
        'The soft light inside pills, circles and tiles; the wide glow of tiles and rows is two thirds of it.',
      ),
    },
  ),
  labComponent(
    'LabGhostButton',
    'Ghost button',
    'Buttons',
    'Shared',
    'The quiet round button: the lock and trash circles of an odds row. The header and toolbar buttons follow it until their own knobs are set.',
    {
      fill: colorKnob('Fill', 'rgba(255,255,255,0.07)', 'Filters, sort, Reset Odds, Back, and the lock and trash circles.'),
      border: colorKnob('Border', 'rgba(255,255,255,0.09)'),
    },
  ),
  labComponent(
    'LabImagePlaceholder',
    'Image placeholder',
    'Misc',
    'Shared',
    'What a card shows while it has no photo: catalog tiles, rail rows, slabs, odds rows, the pack bar, the result list and the winner.',
    {
      fillFrom: colorKnob('Gradient — top left', 'rgba(255,255,255,0.1)'),
      fillTo: colorKnob('Gradient — bottom right', 'rgba(255,255,255,0.02)'),
      icon: colorKnob('Picture icon', 'rgba(255,255,255,0.3)'),
    },
  ),

  // ── Header ──────────────────────────────────────────────────────────
  labComponent(
    'LabHeader',
    'Header',
    'Layout',
    'Header',
    'The title and the line under it, over the catalog and over the odds list.',
    {
      title: inheritKnob('Title', 'LabSurface', 'text'),
      subtitle: inheritKnob('Subtitle', 'LabSurface', 'subtext', 'Also the hint over the odds list.'),
    },
  ),
  labComponent(
    'LabHeaderButton',
    'Header buttons',
    'Buttons',
    'Header',
    'Sound, balance and My Packs at the top right; Back, Reset Odds and Sort on the odds step.',
    {
      fill: inheritKnob('Fill', 'LabGhostButton', 'fill'),
      border: inheritKnob('Border', 'LabGhostButton', 'border'),
      text: inheritKnob('Icon and label', 'LabSurface', 'text'),
    },
  ),

  // ── Catalog ─────────────────────────────────────────────────────────
  labComponent(
    'LabCategoryPill',
    'Category pill',
    'Buttons',
    'Catalog',
    'The row of category pills over the catalog grid.',
    {
      fill: colorKnob('Fill', 'rgba(0,28,54,0.2)', 'Filter chips and a picked toolbar pill follow it until their own knobs are set.'),
      border: colorKnob('Border', 'rgba(255,255,255,0.13)'),
      activeFill: colorKnob('Active — top glow', 'rgba(255,255,255,0.1)'),
      activeLine: colorKnob('Active — bottom line', '#FFFFFF'),
      text: inheritKnob('Label', 'LabSurface', 'text'),
    },
  ),
  labComponent(
    'LabCardTile',
    'Card tile',
    'Panels',
    'Catalog',
    'A card in the catalog grid: the tile, the outline of a picked card, the edge of the photo and the blocks shown while the grid loads.',
    {
      fill: colorKnob('Fill', 'rgba(255,255,255,0.04)'),
      selectedFill: colorKnob(
        'Picked — accent',
        'rgba(31,217,25,0.95)',
        'The count on Filters, the checked boxes and the Select all / Clear links follow it until their own knobs are set.',
      ),
      selectedLine: colorKnob('Picked — outline', '#1FD919', 'The outline round a card once it is picked.'),
      photoRing: inheritKnob('Photo edge', 'LabSurface', 'border', 'The hairline round the photo of a slab.'),
      placeholder: inheritKnob('Loading blocks', 'LabGhostButton', 'fill', 'The grey blocks of a tile still loading.'),
      grade: inheritKnob('Grade', 'LabSurface', 'subtext'),
    },
  ),

  // ── Toolbar & filters ───────────────────────────────────────────────
  labComponent(
    'LabSearch',
    'Search field',
    'Misc',
    'Toolbar & filters',
    'The search field in the catalog toolbar.',
    {
      border: colorKnob('Border', 'rgba(255,255,255,0.09)'),
      borderFocus: colorKnob('Border — focused', 'rgba(255,255,255,0.25)'),
      fill: colorKnob('Fill', 'transparent'),
      icon: inheritKnob('Magnifier', 'LabSurface', 'subtext', 'The placeholder text takes the Subtext of the surface.'),
    },
  ),
  labComponent(
    'LabToolbarPill',
    'Toolbar pills',
    'Buttons',
    'Toolbar & filters',
    'Filters, Price and Sort in the catalog toolbar, and the count on Filters. A pill is picked while its filter is on.',
    {
      fill: inheritKnob('Fill', 'LabGhostButton', 'fill'),
      border: inheritKnob('Border', 'LabGhostButton', 'border'),
      pickedFill: inheritKnob('Picked — fill', 'LabCategoryPill', 'fill'),
      pickedLine: inheritKnob('Picked — border', 'LabCategoryPill', 'activeLine'),
      pickedGlow: inheritKnob('Picked — glow', 'LabCategoryPill', 'activeFill'),
      badgeFill: inheritKnob('Count — fill', 'LabCardTile', 'selectedFill'),
      badgeText: inheritKnob('Count — number', 'LabSurface', 'text'),
    },
  ),
  labComponent(
    'LabMenu',
    'Drop-down menu',
    'Panels',
    'Toolbar & filters',
    'The Price and Sort menus that open under their toolbar pills.',
    {
      fill: colorKnob('Fill', '#171B12'),
      border: inheritKnob('Border', 'LabSurface', 'border'),
      hover: colorKnob('Option under the pointer', 'rgba(255,255,255,0.06)', 'Also a row of the filters drawer.'),
    },
  ),
  labComponent(
    'LabOverlay',
    'Drawer and sheet',
    'Modals',
    'Toolbar & filters',
    'The filters drawer (a bottom sheet on a phone), the pack sheet, and the dimmed backdrop behind them.',
    {
      backdrop: colorKnob('Backdrop', 'rgba(0,0,0,0.62)'),
      backdropBlur: radiusKnob('Backdrop blur (px)', 2),
      fill: inheritKnob('Fill', 'LabSurface', 'fill'),
      border: inheritKnob('Border', 'LabSurface', 'border'),
      handle: colorKnob('Grab handle', 'rgba(255,255,255,0.24)', 'The bar at the top of a sheet on a phone.'),
    },
  ),
  labComponent(
    'LabCheckbox',
    'Check box',
    'Misc',
    'Toolbar & filters',
    'The check boxes of the filters drawer.',
    {
      border: colorKnob('Border', 'rgba(255,255,255,0.32)'),
      checkedFill: inheritKnob('Checked — fill', 'LabCardTile', 'selectedFill'),
      check: inheritKnob('Check mark', 'LabSurface', 'text'),
    },
  ),
  labComponent(
    'LabFilterChip',
    'Filter chip',
    'Buttons',
    'Toolbar & filters',
    'The chips of the filters drawer, and its Select all and Clear links.',
    {
      fill: inheritKnob('Fill', 'LabCategoryPill', 'fill'),
      border: inheritKnob('Border', 'LabCategoryPill', 'border'),
      pickedLine: inheritKnob('Picked — border', 'LabCategoryPill', 'activeLine'),
      pickedGlow: inheritKnob('Picked — glow', 'LabCategoryPill', 'activeFill'),
      link: inheritKnob('Select all / Clear', 'LabCardTile', 'selectedFill'),
    },
  ),

  // ── Rail ────────────────────────────────────────────────────────────
  labComponent(
    'LabStage',
    'Rail stage',
    'Panels',
    'Rail',
    'The top of the rail: the stars and rings, the pack under its two spotlights, and the carousel of slabs.',
    {
      fill: colorKnob('Background', '#000000'),
      star: colorKnob('Stars', '#FFFFFF'),
      orbit: colorKnob('Rings', 'rgba(255,255,255,0.24)'),
      slab: colorKnob('Slab glass', 'rgba(255,255,255,0.11)', "A slab's glass; the empty slab's outline is half of it."),
      beam: colorKnob('Spotlights', 'rgba(255,244,214,0.55)', 'The two beams on the pack before the first card is picked.'),
      pack: colorKnob('Default pack foil', '#46D940', "The lab's own pack art, shown when no pack image is uploaded."),
      lamp: inheritKnob('Spotlight core', 'LabSurface', 'text', 'The hot centre of each spotlight.'),
      caption: inheritKnob('Caption', 'LabSurface', 'subtext', 'The line under the pack before the first card.'),
      slabRadius: radiusKnob('Slab radius (px)', 8),
    },
  ),
  labComponent(
    'LabRail',
    'Rail panel',
    'Panels',
    'Rail',
    "The rail's border, the list of the cards picked, and the footer with the pack's figures.",
    {
      fill: colorKnob('Footer tint', 'rgba(255,255,255,0.04)', 'The panel above the footer takes the Stage background.'),
      border: colorKnob('Panel border', '#16200F'),
      texture: colorKnob('Contour lines', 'rgba(255,255,255,0.12)', 'The lines on the footer.'),
      row: colorKnob('Selected row', 'rgba(255,255,255,0.04)'),
      rowActive: colorKnob('Last added row outline', 'rgba(31,217,25,0.35)'),
      figureLabel: inheritKnob('Figure — label', 'LabSurface', 'subtext', 'What a footer figure is: Cards, Pack price.'),
      figureValue: inheritKnob('Figure — value', 'LabSurface', 'text'),
    },
  ),
  labComponent(
    'LabStatus',
    'Status block',
    'Panels',
    'Rail',
    '"Your pack is ready" and its progress ring, between the stage and the list.',
    {
      fill: colorKnob('Background', 'rgba(14,20,10,0.62)'),
      ring: colorKnob('Progress ring', '#37D731'),
      track: colorKnob('Ring track', 'rgba(255,255,255,0.1)', 'The part of the ring not filled yet.'),
      title: inheritKnob('Title', 'LabSurface', 'text'),
      hint: inheritKnob('Hint', 'LabSurface', 'subtext'),
    },
  ),
  labComponent(
    'LabCta',
    'Call to action',
    'Buttons',
    'Rail',
    "The footer's call to action and the lab's other filled buttons, and the glow behind a footer that is ready.",
    {
      fill: colorKnob('Fill', 'rgba(31,217,25,0.9)'),
      glow: colorKnob('Glow', '#1FD919', 'The glow behind a footer that is ready.'),
      text: colorKnob('Label colour', '#FFFFFF'),
      border: inheritKnob('Border', 'LabSurface', 'border'),
      disabledFill: colorKnob('Disabled — fill', 'rgba(255,255,255,0.06)'),
      disabledText: colorKnob('Disabled — label', 'rgba(255,255,255,0.28)'),
    },
  ),

  // ── Odds ────────────────────────────────────────────────────────────
  labComponent(
    'LabOddsRow',
    'Odds row',
    'Panels',
    'Odds',
    'A card on the odds step: its row, the lock circle of a locked chance, and the outline of a card that is gone.',
    {
      fill: colorKnob('Fill', 'rgba(255,255,255,0.04)'),
      active: colorKnob('Last edited outline', 'rgba(31,217,25,0.25)'),
      lockedFill: colorKnob('Locked — lock circle', 'rgba(70,217,64,0.28)'),
      gone: inheritKnob('Gone — outline and note', 'LabSurface', 'danger', 'A card no longer in the catalog.'),
    },
  ),
  labComponent(
    'LabSlider',
    'Odds slider',
    'Misc',
    'Odds',
    'The chance slider of an odds row and the number beside it.',
    {
      track: colorKnob('Track', 'rgba(255,255,255,0.06)'),
      fill: colorKnob('Fill', '#46D940', 'A locked or disabled slider shows its fill and knob dimmed.'),
      knob: colorKnob('Knob', '#FFFFFF'),
      chanceText: inheritKnob('Chance', 'LabSurface', 'text', 'The percentage typed beside the slider.'),
    },
  ),
  labComponent(
    'LabNotice',
    'Add cards notice',
    'Panels',
    'Odds',
    'The notice over the odds list that asks for more cards, and its badge.',
    {
      fill: inheritKnob('Fill', 'LabRail', 'fill'),
      border: inheritKnob('Border', 'LabSurface', 'border'),
      accent: inheritKnob('Accent', 'LabCta', 'glow', 'The light from its left edge and the badge.'),
    },
  ),

  // ── Reveal ──────────────────────────────────────────────────────────
  labComponent(
    'LabRevealStage',
    'Reveal stage',
    'Reveal',
    'Reveal',
    'The box the pack opens in, after Rip.',
    {
      fill: inheritKnob('Background', 'LabStage', 'fill'),
      border: inheritKnob('Border', 'LabRail', 'border'),
    },
  ),
  labComponent(
    'LabRevealWinner',
    'Winning card',
    'Reveal',
    'Reveal',
    'The card won: its slab, its name band and the light behind it. Rare and up glow in their tier colour instead.',
    {
      cardFill: inheritKnob('Slab', 'LabStage', 'slab'),
      glow: inheritKnob('Glow — below Rare', 'LabStage', 'beam'),
      name: inheritKnob('Name', 'LabSurface', 'text'),
      grade: inheritKnob('Grade', 'LabSurface', 'subtext'),
    },
  ),
  labComponent(
    'LabResult',
    'Result list',
    'Reveal',
    'Reveal',
    "The pack's cards after the reveal: the column headings, the row of the card won and its PULLED chip.",
    {
      wonRow: colorKnob('Won row', 'rgba(90,227,86,0.136)', "The rail's row with a tenth of the call to action's glow."),
      pulledFill: inheritKnob('PULLED chip — fill', 'LabCta', 'fill'),
      pulledText: inheritKnob('PULLED chip — label', 'LabCta', 'text'),
      header: inheritKnob('Column headings', 'LabSurface', 'subtext'),
    },
  ),

  // ── Phone bar & sheet ───────────────────────────────────────────────
  labComponent(
    'LabPackBar',
    'Pack bar',
    'Panels',
    'Phone bar & sheet',
    'The bar along the bottom of a phone with the cards picked; it opens the pack sheet (see Drawer and sheet).',
    {
      fill: inheritKnob('Fill', 'LabRail', 'fill'),
      border: inheritKnob('Border', 'LabRail', 'border'),
      handle: colorKnob('Grab handle', 'rgba(255,255,255,0.25)'),
      thumbFill: inheritKnob('Card thumbnail — fill', 'LabStage', 'slab'),
      thumbBorder: inheritKnob('Card thumbnail — edge', 'LabRail', 'border'),
    },
  ),
];
